import type { AssessmentV2State, Chapter } from "./types";
import { DEFAULT_BAND, type Band } from "./band";
import { isHobbyId } from "./causes";
import { QUIZ_SCREEN_IDS, TRAIT_SCREEN_IDS } from "./v1port/blocks";
import { skillsScreensFor } from "./skillsEvidence";

/**
 * "What you can already do" — Skills Intelligence™ evidence, asked inside the
 * walk so the report's "Skills that carry you forward" describes the learner
 * rather than the jobs they matched with. Evidence only: what you've done,
 * used or shipped. Nothing here filters a career out.
 */
function chapterCanAlreadyDo(band: Band): Chapter {
  return {
    id: "can_already_do",
    title: "What you can already do",
    blurb: "Things you've done, used or made. Nothing here is a test.",
    screens: skillsScreensFor(band),
  };
}

/**
 * v2 uses chapter titles instead of a step counter. Never surface
 * "step N of M" — it just tells the learner how much suffering is left.
 *
 * Chapters are boundaries between interaction rhythms, not equal-sized
 * buckets.
 *
 * BANDS: the two age bands share one component tree. Part 2 is identical
 * for both — same eleven interaction components, same slots, same option
 * weights — and only the wording changes (see `copy.ts`). Part 1 differs,
 * because a 16-year-old is choosing a stream and a 25-year-old is choosing
 * a job. If you ever need to fork a component on band, the fork belongs
 * here in the item set instead.
 */

// Part 2 is shared. Restemmed screens are restemmed in copy.ts, not forked.
const PART_2_HOW_YOU_WORK = [
  "p2_workday_sort",   // W-SORT-001
  "p2_two_offers",     // W-OFFER-001
  "p2_trade_offs",     // W-TRADE-002
  "p2_quick_ones",     // W-BODY-001
  "p2_last_month",     // W-REALITY-001 — corroboration engine
  "p2_grind_test",     // W-GRIND-001
  "p2_collapse",       // W-CLINICAL-001 — MIN with body swipe
];

const PART_2_WHAT_YOUD_CARRY = [
  "p2_bad_day",         // W-BADDAY-001 — highest-evidence archetype signal
  "p2_whose_problem",   // W-PROBLEM-001 — sets impact domain (20% of score)
  "p2_room",            // W-ROOM-001
  "p2_focus_slider",    // W-FOCUS-001
  "p2_people_battery",  // W-BATTERY-001
  "p2_hard_day",        // W-HARDDAY-001
  "p2_working_pattern", // W-SCHED-001
  "p2_presenting",      // W-SPEAK-001
];

const PART_2_WHERE_GOING = [
  "p2_ten_years",           // W-TENYEAR-001
  "p2_scenario_deadline",   // W-WRONG-001
  "p2_scenario_month_off",  // W-MONTH-001
  "p2_scenario_failure",    // W-ROCKET-001
  "p2_scenario_teammate",   // W-STRUGGLE-001
  "p2_scenario_open_week",  // W-BLANK-001
  "p2_creative_room",       // W-CREATE-001
  "p2_here_is_what",        // W-CONFIRM-001 — review
];

/**
 * The ported v1 instrument. Same two chapters in all three bands — the items
 * are identical and only their stems change with age (see v1port/blocks.ts).
 * Everything answered here reaches the engine through a capped nudge, so the
 * order relative to Part 2 does not change any result.
 */
const CHAPTER_SEE_YOURSELF: Chapter = {
  id: "see_yourself",
  title: "How you see yourself",
  blurb: "The v1 WeKIT\u2122 trait set, carried over. Quick, honest, no right answers.",
  screens: TRAIT_SCREEN_IDS,
};

const CHAPTER_WHAT_PULLS_YOU: Chapter = {
  id: "what_pulls_you",
  title: "What pulls you",
  blurb: "Interests, aptitudes and values \u2014 three short rounds.",
  screens: QUIZ_SCREEN_IDS,
};

const CHAPTERS_18_PLUS: Chapter[] = [
  {
    id: "starting",
    title: "Where you're starting from",
    blurb: "A few things about where you are today. No right answers.",
    screens: [
      "p1_qualification",        // X-QUAL-001
      "p1_study_appetite",       // X-STUDY-001
      "p1_reality_check",        // X-STUDY-002
      "p1_salary_floor",         // X-PAY-001
      "p1_salary_flex",          // X-PAY-002
      "p1_experience_location",  // X-GEO-001
      "p1_background",           // X-BACKGROUND-001 — only if they have experience
    ],
  },
  { id: "how_you_work", title: "How you work", blurb: "How you actually spend your energy — not how you'd like to.", screens: PART_2_HOW_YOU_WORK },
  { id: "what_youd_carry", title: "What you'd carry", blurb: "What still feels worth it when the shine wears off.", screens: PART_2_WHAT_YOUD_CARRY },
  { id: "lights_you_up", title: "What lights you up", blurb: "The things you'd choose on a free afternoon. No wrong answers here.", screens: ["px_passions", "px_hobby_frequency", "px_cause_rank", "px_cause_role"] },
  chapterCanAlreadyDo("18plus"),
  { id: "career_matches", title: "Your career matches", blurb: "Two quick ones so we know what to put in front of you.", screens: ["px_childhood_dream", "px_career_pull"] },
  CHAPTER_SEE_YOURSELF,
  CHAPTER_WHAT_PULLS_YOU,
  { id: "where_going", title: "Where you're going", blurb: "The direction, not the destination.", screens: PART_2_WHERE_GOING },
];

const CHAPTERS_15_18: Chapter[] = [
  {
    id: "starting",
    title: "Where you're starting from",
    blurb: "A few things about school and what comes after. No right answers.",
    screens: [
      "p1_stream",         // X-QUAL-001, restemmed to streams
      "p1_stream_lean",    // X-STREAM-002 — only when the stream is undecided
      "p1_study_appetite", // X-STUDY-001
      "p1_reality_check",  // X-STUDY-002
      "p1_money_matters",  // X-PAY-001, relative only — no LPA at this age
      "p1_study_mobility", // X-MOBILITY-001, replaces X-GEO-001
    ],
  },
  { id: "how_you_work", title: "How you work", blurb: "How you actually spend your energy — not how you'd like to.", screens: PART_2_HOW_YOU_WORK },
  { id: "what_youd_carry", title: "What you'd carry", blurb: "What still feels worth it when the shine wears off.", screens: PART_2_WHAT_YOUD_CARRY },
  { id: "lights_you_up", title: "What lights you up", blurb: "The things you'd choose on a free afternoon. No wrong answers here.", screens: ["px_passions", "px_hobby_frequency", "px_cause_rank", "px_cause_role"] },
  chapterCanAlreadyDo("15to18"),
  { id: "career_matches", title: "Your career matches", blurb: "Two quick ones so we know what to put in front of you.", screens: ["px_childhood_dream", "px_career_pull"] },
  CHAPTER_SEE_YOURSELF,
  CHAPTER_WHAT_PULLS_YOU,
  { id: "where_going", title: "Where you're going", blurb: "The direction, not the destination.", screens: PART_2_WHERE_GOING },
];

/**
 * 12–14 "Explorers".
 *
 * Part 2 is untouched — same three chapters, same 22 screens, same order,
 * same slots. Part 1 is two screens and collects ZERO scoring inputs. A
 * twelve-year-old has no qualification, no salary expectation and no work
 * history; asking for any of them produces a made-up number that then
 * quietly deletes whole families of work from their results. So we don't
 * ask. Both screens here are context only.
 */
const CHAPTERS_12_14: Chapter[] = [
  {
    id: "starting",
    title: "A bit about you",
    blurb: "Two quick things. Nothing here is a test.",
    screens: [
      "p1_school_year",    // context only — stored, never scored
      "p1_what_you_like",  // context only — colours the result copy, nothing else
    ],
  },
  { id: "how_you_work", title: "How you work", blurb: "How you like to spend your energy — not how you think you should.", screens: PART_2_HOW_YOU_WORK },
  { id: "what_youd_carry", title: "What you'd carry", blurb: "What still feels worth doing when it stops being new.", screens: PART_2_WHAT_YOUD_CARRY },
  { id: "lights_you_up", title: "What lights you up", blurb: "The things you'd choose on a free afternoon. No wrong answers here.", screens: ["px_passions", "px_hobby_frequency", "px_cause_rank", "px_cause_role"] },
  chapterCanAlreadyDo("12to14"),
  { id: "career_matches", title: "Your career matches", blurb: "Two quick ones so we know what to put in front of you.", screens: ["px_childhood_dream", "px_career_pull"] },
  CHAPTER_SEE_YOURSELF,
  CHAPTER_WHAT_PULLS_YOU,
  { id: "where_going", title: "Where you're going", blurb: "The direction, not the destination.", screens: PART_2_WHERE_GOING },
];

export function chaptersFor(band: Band = DEFAULT_BAND): Chapter[] {
  if (band === "15to18") return CHAPTERS_15_18;
  if (band === "12to14") return CHAPTERS_12_14;
  return CHAPTERS_18_PLUS;
}

export function allScreensFor(band: Band = DEFAULT_BAND): string[] {
  return chaptersFor(band).flatMap((c) => c.screens);
}

/** Kept for the 18+ callers that predate bands. */
export const CHAPTERS = CHAPTERS_18_PLUS;
export const ALL_SCREENS = allScreensFor("18plus");

/**
 * Conditional screens. X-STREAM-002 only exists for a student who hasn't
 * chosen a stream — asking it of someone already in PCB would be noise.
 */
export function isScreenSkipped(screenId: string, state: AssessmentV2State): boolean {
  if (screenId === "p1_stream_lean") return state.part1.stream !== "undecided";
  // X-BACKGROUND-001 — history is only asked of someone who has told us they
  // have paid experience. A student has no history to give, and inventing one
  // would colour their whole result.
  if (screenId === "p1_background") {
    return state.band !== "18plus" || (state.part1.years_experience ?? 0) < 1;
  }
  // X-HOBBY-002 — only asked about activities the learner actually picked.
  // With no Gen-Z-style activity among their three, there is nothing to
  // ask a frequency about, and an empty screen is worse than no screen.
  if (screenId === "px_hobby_frequency") {
    return !(state.part1.passion_ids ?? []).some(isHobbyId);
  }
  // X-CAUSE-ROLE-001 — only when at least one cause was ranked.
  if (screenId === "px_cause_role") {
    return (state.part1.cause_rank_ids ?? []).length === 0;
  }
  return false;
}

/** Screens actually served to this learner, after conditionals. */
export function activeScreens(state: AssessmentV2State): string[] {
  return allScreensFor(state.band).filter((id) => !isScreenSkipped(id, state));
}

export function chapterForScreen(screenId: string, band: Band = DEFAULT_BAND): Chapter | null {
  return chaptersFor(band).find((c) => c.screens.includes(screenId)) ?? null;
}

export function progressFraction(screenId: string, state?: AssessmentV2State): number {
  const list = state ? activeScreens(state) : ALL_SCREENS;
  const idx = list.indexOf(screenId);
  if (idx < 0) return 0;
  return (idx + 1) / list.length;
}

export function nextScreen(screenId: string, state?: AssessmentV2State): string | null {
  const list = state ? activeScreens(state) : ALL_SCREENS;
  const idx = list.indexOf(screenId);
  if (idx < 0 || idx >= list.length - 1) return null;
  return list[idx + 1];
}

export function prevScreen(screenId: string, state?: AssessmentV2State): string | null {
  const list = state ? activeScreens(state) : ALL_SCREENS;
  const idx = list.indexOf(screenId);
  if (idx <= 0) return null;
  return list[idx - 1];
}

/** Total decisions served, for the intro copy. Conditionals make this vary. */
export function decisionCount(band: Band): number {
  // Every band carries px_hobby_frequency conditionally; 15–18 and 18+ each
  // carry one more conditional on top of it.
  return allScreensFor(band).length - (band === "12to14" ? 2 : 3);
}
